import { useMemo, type ReactNode } from 'react';
import type { Episode, FrameRecord, FrameState, MinutePoint } from '@erectus/shared';
import { STATE_LABEL, STATE_STYLE } from '@/scoring/frameState.js';
import { Icon } from '@/ui/icons.js';

/**
 * Session timeline heatmap. Frames are bucketed into fixed columns so a 3-hour
 * session renders in the same space as a 15-minute one.
 */
const COLUMNS = 60;

export function Timeline({
  frames,
  durationSec,
  onPick,
  selection,
}: {
  frames: readonly FrameRecord[];
  durationSec: number;
  onPick?(second: number): void;
  selection?: { start: number; end: number } | null;
}): ReactNode {
  const columns = useMemo(() => bucketStates(frames, durationSec), [frames, durationSec]);

  if (columns.length === 0) {
    return (
      <p className="text-sm text-muted">
        The moment-by-moment detail stays on the phone that recorded the session, for 7 days.
      </p>
    );
  }

  return (
    <div>
      <div className="flex h-10 w-full gap-px overflow-hidden rounded-lg" role="img" aria-label="Posture over the session">
        {columns.map((state, i) => {
          const second = ((i + 0.5) / COLUMNS) * durationSec;
          const selected =
            selection && second >= selection.start && second <= selection.end ? true : false;
          return (
            <button
              key={i}
              type="button"
              aria-label={`${formatMinute(second)}: ${STATE_LABEL[state]}`}
              onClick={onPick ? () => onPick(Math.round(second)) : undefined}
              className={`h-full flex-1 ${onPick ? 'cursor-pointer' : 'cursor-default'} ${
                selected ? 'outline outline-2 -outline-offset-2 outline-ink' : ''
              }`}
              style={{ backgroundColor: STATE_STYLE[state].color, opacity: state === 'invalid' ? 0.3 : 1 }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-xs text-muted">
        <span>0:00</span>
        <span>{formatMinute(durationSec)}</span>
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {(['good', 'forwardHead', 'slouch', 'recline', 'invalid'] as FrameState[]).map((state) => (
          <li key={state} className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 rounded-sm"
              style={{ backgroundColor: STATE_STYLE[state].color }}
            />
            {STATE_LABEL[state]}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Pick the most common state in each column — the honest summary of a bucket. */
export function bucketStates(frames: readonly FrameRecord[], durationSec: number): FrameState[] {
  if (frames.length === 0 || durationSec <= 0) return [];
  const buckets: Array<Partial<Record<FrameState, number>>> = Array.from(
    { length: COLUMNS },
    () => ({}),
  );
  for (const frame of frames) {
    const index = Math.min(
      COLUMNS - 1,
      Math.max(0, Math.floor((frame.t / 1000 / durationSec) * COLUMNS)),
    );
    const bucket = buckets[index]!;
    bucket[frame.state] = (bucket[frame.state] ?? 0) + 1;
  }
  return buckets.map((bucket) => {
    const entries = Object.entries(bucket) as Array<[FrameState, number]>;
    if (entries.length === 0) return 'invalid';
    return entries.sort((a, b) => b[1] - a[1])[0]![0];
  });
}

/** A trailing block shorter than this is left off: 30 s of good posture is not "100%". */
const MIN_BLOCK_SEC = 2 * 60;
/** Report blocks are five minutes (2026-10-16): checks are 15 or 30 min, so 15-minute blocks said little. */
export const BLOCK_MIN = 5;

/**
 * Good posture per block, from the per-minute series: each minute counts by
 * how much of it was measurable. Null when the session has no series (very
 * old ones) — then the stored `decay` buckets are shown instead.
 */
export function blocksFromSeries(series: readonly MinutePoint[] | undefined, durationSec: number, blockMin = BLOCK_MIN): number[] | null {
  if (!series || series.length === 0) return null;
  const count = Math.ceil(durationSec / 60 / blockMin);
  const good = new Array<number>(count).fill(0);
  const valid = new Array<number>(count).fill(0);
  for (const point of series) {
    const i = Math.min(count - 1, Math.floor(point.m / blockMin));
    good[i]! += point.good * point.valid;
    valid[i]! += point.valid;
  }
  return good.map((g, i) => (valid[i]! > 0 ? g / valid[i]! : 0));
}

/**
 * Good posture per block (ARCHITECTURE §1.5), five minutes each. Always
 * shown; the trend needs two or more blocks.
 */
export function DecayBars({
  decay,
  durationSec,
  blockSec: BLOCK_SEC,
}: {
  decay: readonly number[];
  durationSec: number;
  blockSec: number;
}): ReactNode {
  if (decay.length === 0) return <p className="text-sm text-muted">Nothing was measured in this session.</p>;
  const blockMin = Math.round(BLOCK_SEC / 60);
  const lastLength = durationSec - (decay.length - 1) * BLOCK_SEC;
  const blocks = decay.length > 1 && lastLength < MIN_BLOCK_SEC ? decay.slice(0, -1) : decay;
  const color = (ratio: number): string =>
    ratio >= 0.7 ? 'var(--color-state-good)' : ratio >= 0.4 ? 'var(--color-state-fhp)' : 'var(--color-state-slouch)';
  // A gapless grid (each cell pads its own bar) puts column centres at exactly
  // (i + ½) / N, so the trend line meets the bar tops.
  const n = blocks.length;
  const points = blocks.map((ratio, i) => ({ x: ((i + 0.5) / n) * 100, y: 100 - Math.max(3, ratio * 100) }));
  const grid = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, maxWidth: `${n * 6}rem` };
  return (
    <div>
      <div className="grid text-center" style={grid}>
        {blocks.map((ratio, i) => (
          <span key={i} className="text-xs font-semibold text-ink">
            {Math.round(ratio * 100)}%
          </span>
        ))}
      </div>
      <div className="relative mt-1 h-16" style={{ maxWidth: grid.maxWidth }}>
        <div className="grid h-full" style={grid}>
          {blocks.map((ratio, i) => {
            const end = Math.min((i + 1) * BLOCK_SEC, durationSec);
            return (
              <div key={i} className="h-full px-1">
                <div className="flex h-full w-full items-end overflow-hidden rounded bg-surface">
                  <div
                    className="w-full rounded-t"
                    style={{ height: `${Math.max(3, ratio * 100)}%`, backgroundColor: color(ratio), opacity: n >= 2 ? 0.45 : 1 }}
                    role="img"
                    aria-label={`Minutes ${i * blockMin}–${Math.round(end / 60)}: ${Math.round(ratio * 100)}% good posture`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {n >= 2 ? (
          <>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
              <polyline
                points={points.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="var(--color-ink)"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {points.map((p) => (
              <span
                key={p.x}
                aria-hidden
                className="pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
              />
            ))}
          </>
        ) : null}
      </div>
      <div className="mt-1 grid text-center" style={grid}>
        {blocks.map((_, i) => (
          <span key={i} className="text-[10px] text-muted">
            {i * blockMin}–{Math.round(Math.min((i + 1) * BLOCK_SEC, durationSec) / 60)}′
          </span>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted">
        {blocks.length < 2
          ? 'How much of the session you sat well.'
          : `Each bar is ${blockMin} minutes of this session, in order: the taller the bar, the more of those minutes you sat well. Bars that shrink from left to right mean your posture wore down the longer you sat.`}
      </p>
    </div>
  );
}

export function EpisodeList({ episodes }: { episodes: readonly Episode[] }): ReactNode {
  if (episodes.length === 0) {
    return <p className="text-sm text-muted">No episode lasted 20 seconds or more. Nice.</p>;
  }
  return (
    <ul className="space-y-2">
      {episodes.map((episode, i) => (
        <li key={`${episode.start}-${i}`} className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <Icon
              name={STATE_STYLE[episode.state].icon}
              size={18}
              style={{ color: STATE_STYLE[episode.state].color }}
            />
            {STATE_LABEL[episode.state]}
          </span>
          <span className="text-muted">
            {formatMinute(episode.start)} · {Math.round(episode.end - episode.start)}s
            {episode.recovered ? ' · recovered' : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function formatMinute(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
